import { options } from '!/flags'
import { register } from '!/repository'
import { model as router } from '!/router'
import { model as bannersModel } from '$/banners'
import { model as channelsModel } from '$/tv/сhannels'
import { genPagesLogic } from '@/effector'
import { T } from '@/helpers'
import { api, type TvChannelUpdateObj } from '@setplex/tria-api'
import { createEvent, createStore, restore, sample } from 'effector'
import { and, not, or } from 'patronum'
import { model as tvModel } from '~/entities/tv'
import { model as tvBundlesModel } from '~/entities/tv/bundles'
import { model as tvCategoriesModel } from '~/entities/tv/categories'
import { model as tvFeaturedModel } from '~/entities/tv/featured'
import '~/shared/ui/translations/general-translations'

const DEFAULT_ROW_ELEMENTS_COUNT = 36
const DEFAULT_ROWS_COUNT = 9

export const init = createEvent<{ query: URLSearchParams }>()
export const left = createEvent()
export const updateTvChannel = createEvent<TvChannelUpdateObj>()

export const load = createEvent<{
  page: number
  count?: number
}>()

/** Skeleton start*/
export const setContentLoading = createEvent<boolean>()

export const $contentLoading = restore(setContentLoading, false)

/** Skeleton end*/

// CONNECTIONS

sample({
  clock: init,
  fn: ({ query }) => ({
    page: Number(query.get('page')) || 0,
    count: Number(query.get('count')) || DEFAULT_ROWS_COUNT,
  }),
  target: load,
})

sample({
  clock: updateTvChannel,
  fn: ({ favorite, id }) => ({ favorite: !favorite, id }),
  target: api.tv.updateTvChannelFx,
})

sample({
  clock: load,
  filter: not(api.tvBundles.initBundlesRowByPageFx.pending),
  fn: (clockData) => ({ ...clockData, count: DEFAULT_ROW_ELEMENTS_COUNT }),
  target: api.tvBundles.initBundlesRowByPageFx,
})
sample({
  clock: load,
  filter: not(api.tv.initInfiniteChannelsCategoryFx.pending),
  fn: (clockData) => ({ ...clockData, count: DEFAULT_ROW_ELEMENTS_COUNT }),
  target: api.tv.initInfiniteChannelsCategoryFx,
})
sample({
  clock: load,
  filter: not(api.tv.loadCategoriesWithChannelsFx.pending),
  target: api.tv.loadCategoriesWithChannelsFx, // TODO use init effect
})
sample({
  clock: load,
  filter: not(api.banners.getBannersFx.pending),
  target: api.banners.getBannersFx,
})
sample({
  clock: load,
  filter: not(api.banners.getFeaturedCarouselFx.pending),
  target: api.banners.getFeaturedCarouselFx,
})

const loadMoreTvCategories = genPagesLogic(
  api.tv.$pageInfoCategoriesWithChannels,
  api.tv.loadCategoriesWithChannelsFx
)

/********** Skeleton logics starts ***********/
const $isAnyInitFxPending = or(
  // TODO should be only attach fx`s there
  api.banners.getBannersFx.pending,
  api.banners.getFeaturedCarouselFx.pending,
  api.tvBundles.initBundlesRowByPageFx.pending,
  api.tv.initCategoriesWithChannelsFx.pending, // TODO: does this effect is really need here?
  api.tv.initInfiniteChannelsCategoryFx.pending,
  api.tv.loadCategoriesWithChannelsFx.pending
)

const $isInitialLoadedCategoriesWithChannels = createStore(false).on(
  api.tv.loadCategoriesWithChannelsFx.doneData,
  T
)

sample({
  clock: $isAnyInitFxPending,
  source: {
    pathname: router.$pathname,
    isInitialLoadedCategoriesWithChannels:
      $isInitialLoadedCategoriesWithChannels,
  },
  filter: ({ pathname }) => pathname === '/modules/tv_channels',
  fn: ({ isInitialLoadedCategoriesWithChannels }, isAnyInitFxPending) =>
    !isInitialLoadedCategoriesWithChannels && isAnyInitFxPending,
  target: setContentLoading,
})
/********** Skeleton logics ends ***********/

const $bannerTvCarouselShown = and(
  options.$bannerCarouselsEnabled,
  bannersModel.$bannersTypeTvFormatted.map((banners) =>
    banners.some(({ bannerBundles }) => bannerBundles?.length)
  )
)

const $featuredTvCarouselShown = and(
  options.$featuredCarouselsEnabled,
  tvFeaturedModel.$featuredCarouselTypeTv.map((carousels) =>
    carousels.some(
      (item) => item.enabledOnModulePage && item.content?.length > 0
    )
  )
)

// clear paginatable stores
api.banners.$bannersTypeTv.reset(init)
api.tvBundles.$tvBundlesRow.reset(init)
api.tv.$tvCategoriesWithChannels.reset(init) // not necessary
$isInitialLoadedCategoriesWithChannels.reset(left)

register(updateTvChannel, 'updateTvChannel')
register(tvModel.seeAllTvClick, 'seeAllTvClick')
register(tvBundlesModel.$tvBundlesRowFormatted, '$tvBundlesRow')
register(tvBundlesModel.$tvBundlesRowTitle, '$tvBundlesRowTitle')
register(bannersModel.$bannersTypeTvFormatted, '$bannersTypeTv')
register($bannerTvCarouselShown, '$bannerTvCarouselShown')
register(tvFeaturedModel.$featuredCarouselTypeTv, '$featuredCarouselTypeTv')
register(tvFeaturedModel.$featuredCarouselTvTitle, '$featuredCarouselTvTitle')
register($featuredTvCarouselShown, '$featuredTvCarouselShown')
register(
  tvCategoriesModel.$tvCategoriesWithChannels,
  '$tvCategoriesWithChannels'
)
register(loadMoreTvCategories, 'loadMoreTvCategories')
register(
  api.tv.$pageInfoCategoriesWithChannels,
  '$pageInfoCategoriesWithChannels'
)
register(channelsModel.$infiniteChannelsFormatted, '$infiniteChannelsFormatted')
register(channelsModel.$infiniteTvTitle, '$infiniteTvTitle')
